import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import moment from "moment";
import { ColDef } from "ag-grid-community";
import {
  DollarSign,
  Percent,
  Wallet,
  Receipt,
  TrendingUp,
  Car,
  User,
  Calendar,
  Eye,
  Filter,
  RotateCcw,
  Search,
} from "lucide-react";

import { GlobalContext } from "../context/Provider";
import henceforthApi from "../utils/henceforthApi";
import PaginationLayout from "../components/PaginationLayout";
import ExportButton, { ExportColumn } from "../components/common/ExportButton";
import CountCard from "../components/countCard";
import AgGridTable from "../components/ui/AgGridTable";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import profile_placeholder from "../assets/images/profile_placeholder.png";
import "./EarningsAndTax.scss";

type TabType = "rides" | "drivers" | "taxes";

interface SummaryData {
  total_completed_rides: number;
  total_gross_revenue: number;
  total_platform_commission: number;
  total_driver_earnings: number;
  total_tax_collected: number;
  total_discount: number;
  net_platform_earnings: number;
  country?: string;
  currency?: string;
}

const COUNTRY_OPTIONS = [
  { label: "🇮🇳 India (INR)", value: "IN", currency: "INR", symbol: "₹" },
  { label: "🇺🇸 United States (USD)", value: "US", currency: "USD", symbol: "$" },
  { label: "🇨🇦 Canada (CAD)", value: "CA", currency: "CAD", symbol: "CA$" },
  { label: "🇳🇬 Nigeria (NGN)", value: "NG", currency: "NGN", symbol: "₦" },
  { label: "🌐 Rest of the World (USD)", value: "ROW", currency: "USD", symbol: "$" },
];

export const formatCurrency = (amount: number | string, currency?: string) => {
  const num = Number(amount) || 0;
  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  switch ((currency || "").toUpperCase()) {
    case "NGN":
      return `₦${formatted}`;
    case "INR":
      return `₹${formatted}`;
    case "CAD":
      return `CA$${formatted}`;
    case "USD":
    default:
      return `$${formatted}`;
  }
};

export const getCountryBadge = (c?: string) => {
  const code = (c || "").toUpperCase();
  switch (code) {
    case "US":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
          <span>🇺🇸</span>US
        </span>
      );
    case "CA":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
          <span>🇨🇦</span>CA
        </span>
      );
    case "NG":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
          <span>🇳🇬</span>NG
        </span>
      );
    case "IN":
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
          <span>🇮🇳</span>IN
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
          <span>🌐</span>{code || "ROW"}
        </span>
      );
  }
};

export const EarningsAndTax: React.FC = () => {
  const { authState, handleError } = useContext(GlobalContext);
  const navigate = useNavigate();
  const params: any = useParams();
  const [searchParams] = useSearchParams();

  if (authState.access_token) {
    henceforthApi.setToken(authState.access_token);
  }

  // Active Tab from URL
  const activeTab: TabType = (searchParams.get("tab") as TabType) || "rides";

  // Applied parameters from URL
  const appliedCountry = searchParams.get("country") || "IN";
  const appliedStatus = searchParams.get("status") || "COMPLETED";
  const appliedSearch = searchParams.get("search") || "";
  const currentPage = Math.max(1, parseInt(params.page || searchParams.get("page") || "1", 10));
  const limit = 10;

  // Local filter controls state (allows typing and selecting without triggering unwanted refetches)
  const [country, setCountry] = useState<string>(appliedCountry);
  const [search, setSearch] = useState<string>(appliedSearch);
  const [status, setStatus] = useState<string>(appliedStatus);

  // Sync local filter state when URL changes externally
  useEffect(() => {
    setCountry(appliedCountry);
  }, [appliedCountry]);

  useEffect(() => {
    setSearch(appliedSearch);
  }, [appliedSearch]);

  useEffect(() => {
    setStatus(appliedStatus);
  }, [appliedStatus]);

  // Loading States
  const [loading, setLoading] = useState<boolean>(false);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(false);

  // Summary State
  const [summary, setSummary] = useState<SummaryData>({
    total_completed_rides: 0,
    total_gross_revenue: 0,
    total_platform_commission: 0,
    total_driver_earnings: 0,
    total_tax_collected: 0,
    total_discount: 0,
    net_platform_earnings: 0,
    country: "US",
    currency: "USD",
  });

  // Table Data States
  const [rideData, setRideData] = useState<any[]>([]);
  const [driverData, setDriverData] = useState<any[]>([]);
  const [taxData, setTaxData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  // 1. Fetch Summary Cards - ONLY depends on country! Changing status or search NEVER alters report summary!
  const fetchSummary = useCallback(
    async (selectedCountry: string) => {
      try {
        setSummaryLoading(true);
        const q = new URLSearchParams();
        if (selectedCountry) q.append("country", selectedCountry);
        const res = await henceforthApi.Earnings.getSummary(q.toString());
        if (res?.data) {
          setSummary(res.data);
        }
      } catch (err: any) {
        handleError(err);
      } finally {
        setSummaryLoading(false);
      }
    },
    [handleError]
  );

  // 2. Fetch Table Data - Tax Report ONLY depends on country! Ride-wise uses status and search.
  const fetchData = useCallback(
    async (
      pageToLoad: number,
      tabToLoad: TabType,
      c: string,
      s: string,
      qSearch: string
    ) => {
      try {
        setLoading(true);

        if (tabToLoad === "rides") {
          const q = new URLSearchParams();
          if (c) q.append("country", c);
          if (s && s !== "ALL") q.append("status", s);
          if (qSearch.trim()) q.append("search", qSearch.trim());
          const res = await henceforthApi.Earnings.getRides(
            pageToLoad,
            limit,
            q.toString()
          );
          const result = res?.data || {};
          setRideData(result.data || []);
          setTotalCount(result.total || 0);
        } else if (tabToLoad === "drivers") {
          const q = new URLSearchParams();
          if (c) q.append("country", c);
          if (qSearch.trim()) q.append("search", qSearch.trim());
          const res = await henceforthApi.Earnings.getDrivers(
            pageToLoad,
            limit,
            q.toString()
          );
          const result = res?.data || {};
          setDriverData(result.data || []);
          setTotalCount(result.total || 0);
        } else if (tabToLoad === "taxes") {
          // Tax Report data ONLY depends on country! Status or search does not alter tax report.
          const q = new URLSearchParams();
          if (c) q.append("country", c);
          const res = await henceforthApi.Earnings.getTaxReport(
            pageToLoad,
            limit,
            q.toString()
          );
          const result = res?.data || {};
          setTaxData(result.data || []);
          setTotalCount(result.total || 0);
        }
      } catch (err: any) {
        handleError(err);
        setTotalCount(0);
        if (tabToLoad === "rides") setRideData([]);
        if (tabToLoad === "drivers") setDriverData([]);
        if (tabToLoad === "taxes") setTaxData([]);
      } finally {
        setLoading(false);
      }
    },
    [handleError, limit]
  );

  // Summary refetches ONLY when country changes!
  useEffect(() => {
    fetchSummary(appliedCountry);
  }, [appliedCountry, fetchSummary]);

  // Table refetches on page, tab, or applied filter navigation
  useEffect(() => {
    fetchData(
      currentPage,
      activeTab,
      appliedCountry,
      appliedStatus,
      appliedSearch
    );
  }, [
    fetchData,
    currentPage,
    activeTab,
    appliedCountry,
    appliedStatus,
    appliedSearch,
  ]);

  // Handle Tab Switch - preserves current country and applied status
  const handleTabChange = (tab: TabType) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    next.set("country", appliedCountry);
    navigate(`/earnings-and-tax/1?${next.toString()}`);
  };

  // Handle Country Change - Report data and summary update for the new country immediately!
  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const next = new URLSearchParams(searchParams);
    next.set("country", newCountry);
    next.set("tab", activeTab);
    if (status && status !== "ALL") next.set("status", status);
    if (search.trim()) next.set("search", search.trim());
    navigate(`/earnings-and-tax/1?${next.toString()}`);
  };

  // Handle Page Change - updates route URL properly
  const handlePageChange = (newPage: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", activeTab);
    next.set("country", appliedCountry);
    navigate(`/earnings-and-tax/${newPage}?${next.toString()}`);
  };

  // Apply filters (Search and Status)
  const handleApplyFilters = () => {
    const next = new URLSearchParams();
    next.set("tab", activeTab);
    next.set("country", country);
    if (status && status !== "ALL") next.set("status", status);
    if (search.trim()) next.set("search", search.trim());
    navigate(`/earnings-and-tax/1?${next.toString()}`);
  };

  // Reset Filters - Keeps the selected country, resets search and status
  const handleResetFilters = () => {
    setStatus("COMPLETED");
    setSearch("");
    const next = new URLSearchParams();
    next.set("tab", activeTab);
    next.set("country", country);
    navigate(`/earnings-and-tax/1?${next.toString()}`);
  };

  // Export Columns Definitions
  const rideExportColumns: ExportColumn[] = [
    { header: "Order / Ride ID", key: "order_id", width: 22 },
    { header: "Currency", key: "currency", width: 12 },
    { header: "Date & Time", key: "date_time", type: "date", width: 20 },
    { header: "Rider Name", key: "rider_name", width: 18 },
    { header: "Driver Name", key: "driver_name", width: 18 },
    { header: "Vehicle Type", key: "vehicle_type", width: 14 },
    { header: "Pickup Location", key: "pickup", width: 24 },
    { header: "Drop Location", key: "drop", width: 24 },
    { header: "Distance/Time", key: "distance", width: 15 },
    { header: "Ride Status", key: "ride_status", width: 14 },
    { header: "Base Fare", key: "fare", type: "currency", isAmount: true, width: 14 },
    { header: "Discount/Promo", key: "discount", type: "currency", isAmount: true, width: 14 },
    { header: "Gross Amount", key: "gross_amount", type: "currency", isAmount: true, width: 16 },
    { header: "Tax Amount", key: "tax_amount", type: "currency", isAmount: true, width: 14 },
    { header: "Tax %", key: "tax_percent", type: "number", width: 10 },
    { header: "Platform Commission", key: "platform_commission", type: "currency", isAmount: true, width: 18 },
    { header: "Commission %", key: "commission_percent", type: "number", width: 14 },
    { header: "Driver Earning", key: "driver_earning", type: "currency", isAmount: true, width: 16 },
    { header: "Payment Method", key: "payment_method", width: 16 },
    { header: "Payment Status", key: "payment_status", width: 14 },
  ];

  const driverExportColumns: ExportColumn[] = [
    { header: "Driver Name", key: "driver_name", width: 20 },
    { header: "Currency", key: "currency", width: 12 },
    { header: "Driver Email", key: "driver_email", width: 24 },
    { header: "Driver Phone", key: "driver_phone", width: 18 },
    { header: "Total Completed Rides", key: "total_rides", type: "number", isAmount: true, width: 18 },
    { header: "Gross Amount", key: "gross_amount", type: "currency", isAmount: true, width: 16 },
    { header: "Platform Commission", key: "platform_commission", type: "currency", isAmount: true, width: 18 },
    { header: "Tax Collected", key: "tax_collected", type: "currency", isAmount: true, width: 16 },
    { header: "Net Payout", key: "net_payout", type: "currency", isAmount: true, width: 16 },
  ];

  const taxExportColumns: ExportColumn[] = [
    { header: "Period / Month", key: "period", width: 16 },
    { header: "Currency", key: "currency", width: 12 },
    { header: "Completed Rides", key: "total_completed_rides", type: "number", isAmount: true, width: 16 },
    { header: "Taxable Fare", key: "taxable_amount", type: "currency", isAmount: true, width: 16 },
    { header: "Tax Collected", key: "tax_collected", type: "currency", isAmount: true, width: 16 },
    { header: "Effective Tax %", key: "effective_tax_rate", type: "number", width: 14 },
    { header: "Gross Revenue", key: "gross_revenue", type: "currency", isAmount: true, width: 16 },
    { header: "Platform Commission", key: "platform_commission", type: "currency", isAmount: true, width: 18 },
    { header: "Driver Earnings", key: "driver_earnings", type: "currency", isAmount: true, width: 16 },
  ];

  const activeCurrency = summary.currency || "USD";

  // AG Grid Column Definitions for Ride-wise Table (Fixed widths and consistent styling)
  const rideColDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Ride ID",
        field: "order_id",
        width: 125,
        minWidth: 120,
        cellRenderer: (p: any) => {
          const id = p.value || p.data?._id?.slice(-8) || "N/A";
          return (
            <span className="font-bold text-[#006747] text-xs tracking-tight">
              #{id}
            </span>
          );
        },
      },
      {
        headerName: "Date & Time",
        field: "date_time",
        width: 155,
        minWidth: 150,
        cellRenderer: (p: any) => {
          const val = p.value;
          if (!val) return <span className="text-slate-400 text-xs">N/A</span>;
          const d = isNaN(Number(val)) ? val : Number(val);
          return (
            <div className="flex flex-col justify-center leading-tight">
              <span className="text-xs font-semibold text-slate-800">
                {moment(d).format("DD MMM YYYY")}
              </span>
              <span className="text-[11px] text-slate-400 font-medium">
                {moment(d).format("hh:mm A")}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Rider",
        field: "rider_name",
        width: 180,
        minWidth: 160,
        cellRenderer: (p: any) => {
          const row = p.data;
          const pic = row?.rider_profile_pic
            ? henceforthApi.FILES.imageOriginal(row.rider_profile_pic)
            : profile_placeholder;
          return (
            <div className="flex items-center gap-2.5 py-1 min-w-0">
              <img
                src={pic}
                alt=""
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = profile_placeholder;
                }}
              />
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-slate-800 text-xs truncate leading-tight">
                  {row?.rider_name || "Unknown"}
                </span>
                <span className="text-[11px] text-slate-400 truncate">
                  {row?.rider_phone || "Rider"}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Driver",
        field: "driver_name",
        width: 180,
        minWidth: 160,
        cellRenderer: (p: any) => {
          const row = p.data;
          const pic = row?.driver_profile_pic
            ? henceforthApi.FILES.imageOriginal(row.driver_profile_pic)
            : profile_placeholder;
          return (
            <div className="flex items-center gap-2.5 py-1 min-w-0">
              <img
                src={pic}
                alt=""
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = profile_placeholder;
                }}
              />
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-slate-800 text-xs truncate leading-tight">
                  {row?.driver_name || "Unassigned"}
                </span>
                <span className="text-[11px] text-slate-400 truncate">
                  {row?.driver_phone || "Driver"}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Vehicle",
        field: "vehicle_type",
        width: 115,
        minWidth: 110,
        cellRenderer: (p: any) => {
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
              {p.value || "Standard"}
            </span>
          );
        },
      },
      {
        headerName: "Route",
        width: 230,
        minWidth: 200,
        cellRenderer: (p: any) => {
          const row = p.data;
          return (
            <div className="flex flex-col justify-center gap-1 py-1 min-w-0 w-full">
              <div
                className="flex items-center gap-1.5 text-xs text-slate-700 truncate"
                title={row?.pickup}
              >
                <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                <span className="truncate">{row?.pickup || "Pickup"}</span>
              </div>
              <div
                className="flex items-center gap-1.5 text-xs text-slate-500 truncate"
                title={row?.drop}
              >
                <span className="w-2 h-2 rounded-full bg-rose-500 shrink-0"></span>
                <span className="truncate">{row?.drop || "Destination"}</span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Status",
        field: "ride_status",
        width: 130,
        minWidth: 120,
        cellRenderer: (p: any) => {
          const statusVal = p.value as string;
          if (statusVal === "COMPLETED" || statusVal === "CONFIRMED") {
            return <Badge variant="success">{statusVal.replace(/_/g, " ")}</Badge>;
          }
          if (statusVal === "CANCELLED") {
            return <Badge variant="destructive">{statusVal.replace(/_/g, " ")}</Badge>;
          }
          return (
            <Badge variant="warning">
              {statusVal ? statusVal.replace(/_/g, " ") : "PENDING"}
            </Badge>
          );
        },
      },
      {
        headerName: "Base Fare",
        field: "fare",
        width: 115,
        minWidth: 110,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-semibold text-slate-800 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Discount",
        field: "discount",
        width: 110,
        minWidth: 105,
        cellRenderer: (p: any) => {
          const val = Number(p.value);
          return (
            <div className="w-full text-right text-xs">
              <span className={val > 0 ? "text-rose-600 font-semibold" : "text-slate-400"}>
                {val > 0
                  ? `-${formatCurrency(val, p.data?.currency)}`
                  : formatCurrency(0, p.data?.currency)}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Gross Amount",
        field: "gross_amount",
        width: 130,
        minWidth: 125,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-bold text-slate-900 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Tax Amount",
        field: "tax_amount",
        width: 130,
        minWidth: 125,
        cellRenderer: (p: any) => (
          <div className="w-full flex flex-col items-end justify-center leading-tight">
            <span className="font-semibold text-slate-800 text-xs">
              {formatCurrency(p.value, p.data?.currency)}
            </span>
            <span className="text-[10px] text-slate-400 font-medium">
              ({p.data?.tax_percent || 0}%)
            </span>
          </div>
        ),
      },
      {
        headerName: "Platform Comm.",
        field: "platform_commission",
        width: 140,
        minWidth: 135,
        cellRenderer: (p: any) => (
          <div className="w-full flex flex-col items-end justify-center leading-tight">
            <span className="font-semibold text-[#006747] text-xs">
              {formatCurrency(p.value, p.data?.currency)}
            </span>
            <span className="text-[10px] text-slate-400 font-medium">
              ({p.data?.commission_percent || 0}%)
            </span>
          </div>
        ),
      },
      {
        headerName: "Driver Earning",
        field: "driver_earning",
        width: 135,
        minWidth: 130,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-bold text-emerald-700 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Payment",
        width: 135,
        minWidth: 130,
        cellRenderer: (p: any) => {
          const row = p.data;
          const method = row?.payment_method
            ? row.payment_method.replace(/_/g, " ")
            : "CARD";
          const isPaid = row?.payment_status === "COMPLETED";
          return (
            <div className="flex flex-col justify-center leading-tight">
              <span className="text-xs font-medium text-slate-700 capitalize">
                {method}
              </span>
              <span
                className={`text-[10px] font-semibold ${isPaid ? "text-emerald-600" : "text-slate-400"
                  }`}
              >
                {row?.payment_status ? row.payment_status.replace(/_/g, " ") : "COMPLETED"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Action",
        width: 90,
        cellClass: "justify-center",
        cellRenderer: (p: any) => {
          const id = p.data?._id;
          return (
            <Link
              to={`/booking-details/${id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-700 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border border-slate-200 transition-colors shadow-2xs"
            >
              <Eye className="w-3.5 h-3.5 text-slate-500" />
              <span>View</span>
            </Link>
          );
        },
      },
    ];
  }, []);

  // AG Grid Column Definitions for Driver-wise Table (Fixed widths and consistent styling)
  const driverColDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Driver",
        field: "driver_name",
        width: 220,
        minWidth: 200,
        flex: 1.2,
        cellRenderer: (p: any) => {
          const row = p.data;
          const pic = row?.driver_profile_pic
            ? henceforthApi.FILES.imageOriginal(row.driver_profile_pic)
            : profile_placeholder;
          return (
            <div className="flex items-center gap-2.5 py-1 min-w-0">
              <img
                src={pic}
                alt=""
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = profile_placeholder;
                }}
              />
              <div className="flex flex-col min-w-0">
                <span className="font-semibold text-slate-800 text-xs truncate leading-tight">
                  {row?.driver_name || "Unknown"}
                </span>
                <span className="text-[11px] text-slate-400 truncate">
                  ID: {row?.driver_id?.slice(-8) || "N/A"}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: "Email & Phone",
        field: "driver_email",
        width: 220,
        minWidth: 200,
        flex: 1.2,
        cellRenderer: (p: any) => {
          const row = p.data;
          return (
            <div className="flex flex-col justify-center min-w-0 py-1 leading-tight">
              <span className="text-xs text-slate-800 truncate font-medium">
                {row?.driver_email || "N/A"}
              </span>
              <span className="text-[11px] text-slate-400 truncate">
                {row?.driver_phone || "N/A"}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Completed Rides",
        field: "total_rides",
        width: 150,
        minWidth: 140,
        cellClass: "justify-center",
        cellRenderer: (p: any) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-800 border border-slate-200">
            {p.value ?? 0}
          </span>
        ),
      },
      {
        headerName: "Gross Revenue",
        field: "gross_amount",
        width: 160,
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-bold text-slate-900 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Platform Commission",
        field: "platform_commission",
        width: 170,
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-semibold text-[#006747] text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Tax Collected",
        field: "tax_collected",
        width: 160,
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-medium text-slate-600 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Net Driver Payout",
        field: "net_payout",
        width: 170,
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-bold text-emerald-700 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
    ];
  }, []);

  // AG Grid Column Definitions for Tax Report Table (Fixed widths and consistent styling)
  const taxColDefs = useMemo<ColDef[]>(() => {
    return [
      {
        headerName: "Month / Period",
        field: "period",
        width: 200,
        minWidth: 190,
        flex: 1.2,
        cellRenderer: (p: any) => {
          const row = p.data;
          const isMonthlyFormat =
            row?.period &&
            row.period.length === 7 &&
            moment(row.period, "YYYY-MM", true).isValid();
          const formattedPeriod = isMonthlyFormat
            ? moment(row.period, "YYYY-MM").format("MMMM YYYY")
            : row?.period || "N/A";

          return (
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span className="font-bold text-slate-800 text-xs">
                {formattedPeriod}
              </span>
              {isMonthlyFormat && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200">
                  {row.period}
                </span>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Currency",
        field: "currency",
        width: 110,
        minWidth: 100,
        cellClass: "justify-center",
        cellRenderer: (p: any) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            {p.value || "USD"}
          </span>
        ),
      },
      {
        headerName: "Completed Rides",
        field: "total_completed_rides",
        width: 145,
        minWidth: 140,
        cellClass: "justify-center",
        cellRenderer: (p: any) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-800 border border-slate-200">
            {p.value ?? 0}
          </span>
        ),
      },
      {
        headerName: "Taxable Fare",
        field: "taxable_amount",
        width: 150,
        minWidth: 140,
        flex: 1,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-semibold text-slate-800 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Tax Collected (GST/VAT)",
        field: "tax_collected",
        width: 175,
        minWidth: 165,
        flex: 1.1,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-bold text-emerald-700 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Tax Rate",
        field: "effective_tax_rate",
        width: 115,
        minWidth: 105,
        cellClass: "justify-center",
        cellRenderer: (p: any) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-sky-50 text-sky-700 border border-sky-200">
            {p.value ?? 0}%
          </span>
        ),
      },
      {
        headerName: "Gross Revenue",
        field: "gross_revenue",
        width: 155,
        minWidth: 145,
        flex: 1,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-bold text-slate-900 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Platform Commission",
        field: "platform_commission",
        width: 170,
        minWidth: 160,
        flex: 1,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-semibold text-[#006747] text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
      {
        headerName: "Driver Earnings",
        field: "driver_earnings",
        width: 160,
        minWidth: 150,
        flex: 1,
        cellRenderer: (p: any) => (
          <div className="w-full text-right font-semibold text-slate-800 text-xs">
            {formatCurrency(p.value, p.data?.currency)}
          </div>
        ),
      },
    ];
  }, []);

  return (
    <div className="animated fadeInRight earnings-page pb-6">
      {/* 1. Summary KPI Cards with Project-Standard CountCard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-3 mb-4">
        <CountCard
          title={`Gross Revenue (${activeCurrency})`}
          count={
            summaryLoading
              ? "..."
              : formatCurrency(summary.total_gross_revenue, activeCurrency)
          }
          currency=""
          icon={<DollarSign className="w-4 h-4 text-emerald-600" />}
          badgeText="Volume"
          badgeType="trend-up"
          subtitle="Total passenger fares before split"
          iconBgClass="bg-emerald-50 text-emerald-600 border-emerald-100/70"
        />

        <CountCard
          title={`Platform Commission (${activeCurrency})`}
          count={
            summaryLoading
              ? "..."
              : formatCurrency(summary.total_platform_commission, activeCurrency)
          }
          currency=""
          icon={<Percent className="w-4 h-4 text-indigo-600" />}
          badgeText="Fee"
          badgeType="badge-blue"
          subtitle="Total platform fees retained"
          iconBgClass="bg-indigo-50 text-indigo-600 border-indigo-100/70"
        />

        <CountCard
          title={`Driver Earnings (${activeCurrency})`}
          count={
            summaryLoading
              ? "..."
              : formatCurrency(summary.total_driver_earnings, activeCurrency)
          }
          currency=""
          icon={<Wallet className="w-4 h-4 text-emerald-600" />}
          badgeText="Payout"
          badgeType="trend-up"
          subtitle="Net driver earnings payable"
          iconBgClass="bg-emerald-50 text-emerald-600 border-emerald-100/70"
        />

        <CountCard
          title={`Tax Collected (${activeCurrency})`}
          count={
            summaryLoading
              ? "..."
              : formatCurrency(summary.total_tax_collected, activeCurrency)
          }
          currency=""
          icon={<Receipt className="w-4 h-4 text-amber-600" />}
          badgeText="VAT/GST"
          badgeType="badge-gray"
          subtitle="Tax collected for remittance"
          iconBgClass="bg-amber-50 text-amber-600 border-amber-100/70"
        />

        {/* <CountCard
          title={`Net Margin (${activeCurrency})`}
          count={
            summaryLoading
              ? "..."
              : formatCurrency(summary.net_platform_earnings, activeCurrency)
          }
          currency=""
          icon={<TrendingUp className="w-4 h-4 text-teal-600" />}
          badgeText="Margin"
          badgeType="trend-up"
          subtitle="Net profit after discounts"
          iconBgClass="bg-teal-50 text-teal-600 border-teal-100/70"
        /> */}

        <CountCard
          title="Completed Rides"
          count={summaryLoading ? "..." : summary.total_completed_rides}
          currency="Rides"
          icon={<Car className="w-4 h-4 text-sky-600" />}
          badgeText="Completed"
          badgeType="badge-gray"
          subtitle="Fulfilled passenger journeys"
          iconBgClass="bg-sky-50 text-sky-600 border-sky-100/70"
        />
      </div>

      {/* 2. Main Card Container with Toolbar, AgGridTable, and Pagination */}
      <Card className="border border-slate-200/80 shadow-2xs rounded-2xl bg-white overflow-hidden">
        {/* Card Header with Title on Left and Pill Tabs on Right */}
        <div className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-base font-bold text-slate-900 leading-tight">
                {activeTab === "rides" && "Ride-wise Earnings"}
                {activeTab === "drivers" && "Driver-wise Earnings"}
                {activeTab === "taxes" && "Tax Report (Monthly)"}
              </h2>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/70">
                {totalCount} Total
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {activeTab === "rides" &&
                "Detailed breakdown of ride fares, platform commissions, tax, and driver earnings"}
              {activeTab === "drivers" &&
                "Aggregated driver earnings, gross revenue, commissions, and net payouts"}
              {activeTab === "taxes" &&
                "Monthly tax collections, taxable amounts, and effective rates"}
            </p>
          </div>

          {/* Pill Tab Switcher - Fixed Position with Primary Color for Active Tab */}
          <div className="inline-flex items-center bg-slate-100 p-1 rounded-xl border-0">
            <Button
              type="button"
              onClick={() => handleTabChange("rides")}
              variant={activeTab === "rides" ? "default" : "secondary"}
            >
              <Car className="w-3.5 h-3.5" />Ride-wise
            </Button>
            <Button
              type="button"
              onClick={() => handleTabChange("drivers")}
              variant={activeTab === "drivers" ? "default" : "secondary"}
            >
              <User className="w-3.5 h-3.5" /> Driver-wise
            </Button>
            <Button
              type="button"
              onClick={() => handleTabChange("taxes")}
              variant={activeTab === "taxes" ? "default" : "secondary"}
            >
              <Receipt className="w-3.5 h-3.5" /> Tax Report
            </Button>
          </div>
        </div>

        {/* Fixed-Layout Filter Controls Toolbar - Buttons & Controls maintain identical positions in all cases */}
        <div className="p-4 sm:p-5 border-b border-slate-100 bg-slate-50/50 flex flex-wrap items-center justify-between gap-3">
          {/* Left: Fixed Filter Controls Slots */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Search Input - Fixed width slot with guaranteed padding so icon never overlaps text */}
            <div className="relative w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
              <Input
                type="text"
                placeholder={
                  activeTab === "taxes"
                    ? "Tax period (YYYY-MM)..."
                    : activeTab === "drivers"
                      ? "Driver name, phone..."
                      : "Rider, driver, ride ID..."
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleApplyFilters();
                }}
                className="search-icon-input h-9 text-xs bg-white border-slate-200 shadow-2xs"
                style={{ paddingLeft: "36px" }}
              />
            </div>

            {/* Country Select - Fixed width slot (Changing country updates report data!) */}
            <div className="w-56">
              <Select value={country} onValueChange={handleCountryChange}>
                <SelectTrigger className="h-9 text-xs bg-white shadow-2xs border-slate-200">
                  <SelectValue placeholder="Country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_OPTIONS.map((c) => (
                    <SelectItem key={c.value} value={c.value} className="text-xs">
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ride Status Filter - Fixed width slot in all cases (disabled for non-rides so buttons never shift) */}
            <div className="w-40">
              <Select
                value={status}
                onValueChange={(val) => setStatus(val)}
                disabled={activeTab !== "rides"}
              >
                <SelectTrigger
                  className={`h-9 text-xs bg-white shadow-2xs border-slate-200 ${activeTab !== "rides"
                    ? "opacity-45 cursor-not-allowed bg-slate-100/70 text-slate-400"
                    : ""
                    }`}
                  title={activeTab !== "rides" ? "Status filter only applies to Ride-wise table" : undefined}
                >
                  <SelectValue placeholder="Ride Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL" className="text-xs">
                    All Statuses
                  </SelectItem>
                  <SelectItem value="COMPLETED" className="text-xs">
                    Completed
                  </SelectItem>
                  <SelectItem value="CONFIRMED" className="text-xs">
                    Confirmed
                  </SelectItem>
                  <SelectItem value="PENDING" className="text-xs">
                    Pending
                  </SelectItem>
                  <SelectItem value="CANCELLED" className="text-xs">
                    Cancelled
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Right: Fixed Action Buttons Group (Apply, Reset, Export in locked positions) */}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleApplyFilters}
              className="h-9 px-3.5 text-xs font-semibold gap-1.5 shadow-2xs"
            >
              <Filter className="w-3.5 h-3.5" />
              <span>Apply</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleResetFilters}
              title="Reset filters"
              className="h-9 px-3 text-xs text-slate-600 hover:text-slate-900 border-slate-200 shadow-2xs"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>

            {/* Export Button - Fixed position in all cases */}
            <div className="inline-flex">
              {activeTab === "rides" && (
                <ExportButton
                  fileName="ride-earnings"
                  sheetName="Ride Earnings"
                  columns={rideExportColumns}
                  onFetchData={async () => {
                    const q = new URLSearchParams();
                    if (appliedCountry) q.append("country", appliedCountry);
                    if (appliedStatus && appliedStatus !== "ALL")
                      q.append("status", appliedStatus);
                    if (appliedSearch.trim())
                      q.append("search", appliedSearch.trim());
                    q.append("export", "true");
                    const res = await henceforthApi.Earnings.exportRides(
                      q.toString()
                    );
                    return res?.data?.data || res?.data?.rows || [];
                  }}
                  buttonText="Export (.xlsx)"
                  className="h-9 px-3 text-xs font-semibold inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-2xs transition-colors"
                />
              )}
              {activeTab === "drivers" && (
                <ExportButton
                  fileName="driver-earnings"
                  sheetName="Driver Earnings"
                  columns={driverExportColumns}
                  onFetchData={async () => {
                    const q = new URLSearchParams();
                    if (appliedCountry) q.append("country", appliedCountry);
                    if (appliedSearch.trim())
                      q.append("search", appliedSearch.trim());
                    q.append("export", "true");
                    const res = await henceforthApi.Earnings.exportDrivers(
                      q.toString()
                    );
                    return res?.data?.data || res?.data?.rows || [];
                  }}
                  buttonText="Export (.xlsx)"
                  className="h-9 px-3 text-xs font-semibold inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-2xs transition-colors"
                />
              )}
              {activeTab === "taxes" && (
                <ExportButton
                  fileName="tax-report"
                  sheetName="Tax Report"
                  columns={taxExportColumns}
                  onFetchData={async () => {
                    const q = new URLSearchParams();
                    if (appliedCountry) q.append("country", appliedCountry);
                    q.append("export", "true");
                    const res = await henceforthApi.Earnings.exportTaxReport(
                      q.toString()
                    );
                    return res?.data?.data || res?.data?.rows || [];
                  }}
                  buttonText="Export (.xlsx)"
                  className="h-9 px-3 text-xs font-semibold inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-2xs transition-colors"
                />
              )}
            </div>
          </div>
        </div>

        {/* 3. AG Grid Common Table for Selected Tab with Consistent Fixed Layout */}
        <div className="w-full">
          {activeTab === "rides" && (
            <AgGridTable
              rowData={rideData}
              columnDefs={rideColDefs}
              loading={loading}
              rowHeight={56}
              domLayout="autoHeight"
              emptyMessage="No ride earnings records found"
            />
          )}

          {activeTab === "drivers" && (
            <AgGridTable
              rowData={driverData}
              columnDefs={driverColDefs}
              loading={loading}
              rowHeight={56}
              domLayout="autoHeight"
              emptyMessage="No driver earnings records found"
            />
          )}

          {activeTab === "taxes" && (
            <AgGridTable
              rowData={taxData}
              columnDefs={taxColDefs}
              loading={loading}
              rowHeight={56}
              domLayout="autoHeight"
              emptyMessage="No tax report records found"
            />
          )}
        </div>

        {/* 4. Pagination */}
        <div className="p-4 border-t border-slate-100">
          <PaginationLayout
            count={totalCount}
            data={
              activeTab === "rides"
                ? rideData
                : activeTab === "drivers"
                  ? driverData
                  : taxData
            }
            page={currentPage}
            limit={limit}
            loading={loading}
            onPageChange={handlePageChange}
          />
        </div>
      </Card>
    </div>
  );
};

export default EarningsAndTax;
